import { z } from 'zod'

export const AccountRelationshipType = z.enum(['team', 'reseller'])
export type AccountRelationshipType = z.infer<typeof AccountRelationshipType>

export const BillingMode = z.enum(['shared', 'independent'])
export type BillingMode = z.infer<typeof BillingMode>

export const TeamRole = z.enum(['viewer', 'editor', 'manager'])
export type TeamRole = z.infer<typeof TeamRole>

export const InviteStatus = z.enum(['pending', 'accepted', 'expired', 'revoked'])
export type InviteStatus = z.infer<typeof InviteStatus>

export const SubAccountInfo = z.object({
  id: z.number(),
  email: z.string(),
  name: z.string().default('')
})
export type SubAccountInfo = z.infer<typeof SubAccountInfo>

export const AccountRelationship = z.object({
  id: z.number(),
  type: AccountRelationshipType,
  billing_mode: BillingMode,
  label: z.string().nullable().optional(),
  team_role: TeamRole.nullable().optional(),
  granted_permissions: z.array(z.string()).default([]),
  active: z.boolean(),
  created_at: z.string(),
  invite_status: InviteStatus,
  invite_email: z.string().nullable().optional(),
  accepted_at: z.string().nullable().optional(),
  deactivated_at: z.string().nullable().optional(),
  invite_expires_at: z.string().nullable().optional(),
  child_account: SubAccountInfo.nullable().optional(),
  parent_account: SubAccountInfo.nullable().optional()
})
export type AccountRelationship = z.infer<typeof AccountRelationship>

// The backend refuses a label longer than 255 bytes of UTF-8 with
// invalid_request. maxlength counts UTF-16 units, so umlauts slip past it.
export const LABEL_MAX_LENGTH = 255

export const labelTooLong = (label: string): boolean => new TextEncoder().encode(label).length > LABEL_MAX_LENGTH

export type RelationshipStatus = 'active' | 'deactivated' | 'awaitingAcceptance' | Exclude<InviteStatus, 'accepted'>

// A reactivated reseller link stays inactive as an invite, pending or expired
// and renewable, until the customer accepts it again.
export const relationshipStatus = (rel: AccountRelationship): RelationshipStatus => {
  if (!rel.active) {
    const reinvited = rel.invite_status === 'pending' || rel.invite_status === 'expired'
    return reinvited && rel.accepted_at ? 'awaitingAcceptance' : 'deactivated'
  }
  return rel.invite_status === 'accepted' ? 'active' : rel.invite_status
}

// A link where the caller is the child, as GET /account/parents returns it.
export const ParentLink = z.object({
  id: z.number(),
  type: AccountRelationshipType,
  team_role: TeamRole.nullable().optional(),
  billing_mode: BillingMode,
  label: z.string().nullable().optional(),
  accepted_at: z.string().nullable().optional(),
  parent_account: SubAccountInfo.nullable().optional(),
  active: z.boolean().optional(),
  // Pending or expired on a reseller link the reseller reactivated, which
  // waits for the child to accept the invite again.
  invite_status: InviteStatus.optional(),
  can_switch: z.boolean().optional()
})
export type ParentLink = z.infer<typeof ParentLink>

export const InviteSubAccountRequest = z.object({
  email: z.string().email(),
  type: AccountRelationshipType,
  billing_mode: BillingMode.optional(),
  label: z.string().optional(),
  team_role: TeamRole.optional(),
  granted_permissions: z.array(z.string()).optional()
})
export type InviteSubAccountRequest = z.infer<typeof InviteSubAccountRequest>

export const UpdateSubAccountRequest = z.object({
  label: z.string().optional(),
  team_role: TeamRole.optional(),
  billing_mode: BillingMode.optional(),
  granted_permissions: z.array(z.string()).optional()
})
export type UpdateSubAccountRequest = z.infer<typeof UpdateSubAccountRequest>

export const AdminCreateSubAccountRequest = z.object({
  child_account_id: z.number(),
  type: AccountRelationshipType,
  billing_mode: BillingMode.optional(),
  label: z.string().optional(),
  team_role: TeamRole.optional(),
  granted_permissions: z.array(z.string()).optional()
})
export type AdminCreateSubAccountRequest = z.infer<typeof AdminCreateSubAccountRequest>

export const AdminSubAccountListResponse = z.object({
  children: z.array(AccountRelationship),
  parents: z.array(AccountRelationship)
})
export type AdminSubAccountListResponse = z.infer<typeof AdminSubAccountListResponse>

export const InviteDetailsResponse = z.object({
  type: AccountRelationshipType,
  invite_email: z.string().optional(),
  parent_email: z.string().optional(),
  label: z.string().optional(),
  billing_mode: BillingMode.optional(),
  invite_expires_at: z.string().nullable().optional()
})
export type InviteDetailsResponse = z.infer<typeof InviteDetailsResponse>

export const ContextSwitchRequest = z.object({
  target_account_id: z.number()
})
export type ContextSwitchRequest = z.infer<typeof ContextSwitchRequest>

export const ContextSwitchResponse = z.object({
  token: z.string()
})
export type ContextSwitchResponse = z.infer<typeof ContextSwitchResponse>

export const ContextInfoResponse = z.object({
  is_context_switched: z.boolean(),
  acting_as_account_id: z.number().optional(),
  acting_as_type: AccountRelationshipType.optional(),
  original_account_id: z.number().optional()
})
export type ContextInfoResponse = z.infer<typeof ContextInfoResponse>

export const SubAccountDomainInfo = z.object({
  id: z.number(),
  name: z.string(),
  status: z.string(),
  active_until: z.string().nullable().optional(),
  mail_accounts: z.number()
})
export type SubAccountDomainInfo = z.infer<typeof SubAccountDomainInfo>

export const SubAccountResourceEntry = z.object({
  account_id: z.number(),
  email: z.string(),
  name: z.string(),
  label: z.string().nullable().optional(),
  domains: z.array(SubAccountDomainInfo)
})
export type SubAccountResourceEntry = z.infer<typeof SubAccountResourceEntry>

export const SubAccountResourcesResponse = z.object({
  sub_accounts: z.array(SubAccountResourceEntry)
})
export type SubAccountResourcesResponse = z.infer<typeof SubAccountResourcesResponse>
